import React, { useContext, useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    AppBar,
    useMediaQuery,
    useTheme,
    Box,
    Badge,
    IconButton,
    InputBase,
    Typography,
    List,
    ListItemButton,
    ListItem,
    ListItemText,
    Divider,
    Menu,
    MenuItem,
} from '@mui/material';
import {
    Close as CloseIcon,
    Menu as MenuIcon,
    ChevronRight as ChevronRightIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    Search as SearchIcon,
    East as EastIcon,
} from '@mui/icons-material';
import { observer } from 'mobx-react-lite';
import { useUnsavedChanges } from './UnsavedChangesContext';
import StoreContext from '../../react-core-lib/components/app/store-context';
import handoutIcon from '../../assets/icons/Handout.svg';
import feedbackStarIcon from '../../assets/icons/FeedbackStar.svg';
import profilIcon from '../../assets/icons/Profil.svg';
import accessibilityIcon from '../../assets/icons/Accessibility.svg';
import CTAUpgradeDialog from '../UIControls/CTAUpgradeDialog';
import searchSubmitArrowIcon from '../../assets/icons/SearchSubmitArrow.svg';
import { getCTAUpgradeImageUrl, cetraBlueLogo, cetraMobileLogo } from '../../config';
import { AuthState } from '../../stores/auth-store';
import FeedbackModal from './FeedbackModal';
import UserApi from '../../rest-api/user-api';
import clockIcon from '../../assets/icons/Clock.svg';
import { getHandoutBundleUpgradeTarget, isFreeOrStudentPlan } from '../../utils/subscription-plan-utils';
import { parseUserHandouts } from '../../utils/handouts-utils';

const HEADER_HEIGHT = 162;
const RECENT_SEARCHES_LIMIT = 5;

const Navigation = observer(() => {
    const theme = useTheme();
    const isNarrowLandscape = useMediaQuery('(max-width: 920px)');
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
    const showBurgerMenu = isMobileOrTablet || isNarrowLandscape;
    const { promptUnsavedChanges, promptBeforeAction } = useUnsavedChanges();
    const navigate = useNavigate();
    const location = useLocation();
    const appStateStore = useContext(StoreContext);
    const { userDataStore, authStore, contentStore, searchStore, userCreatedHandoutStore } = appStateStore;
    const [upgradeCTAOpen, setUpgradeCTAOpen] = useState(false);
    const [upgradeCTAOpenProfessional, setUpgradeCTAOpenProfessional] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
    const [workspaceMenuAnchor, setWorkspaceMenuAnchor] = useState(null);
    const [searchOpen, setSearchOpen] = useState(false);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const searchRef = useRef(null);
    const isMultiUser = userDataStore?.subscriptionData?.accountType === 'Clinic-multiuser';
    const isIframe = authStore.authState === AuthState.IFRAME_AUTHENTICATED;
    const isHandoutLibraryRoute = location.pathname.startsWith('/search');
    const isModels3DRoute = location.pathname.startsWith('/3d-models');
    const isMyLibraryRoute = location.pathname.startsWith('/my-library');
    const isNewHandoutsRoute = location.pathname.startsWith('/new-handouts');
    const isMyClinicRoute = location.pathname.startsWith('/my-clinic');
    const isHandoutsRoute = location.pathname.startsWith('/handouts');
    const isHelpHubRoute = location.pathname.startsWith('/help-hub');
    const handoutBundleCount = parseUserHandouts(userDataStore?.userData?.handouts).length;
    const isCompactHeaderRoute =
        isHandoutLibraryRoute ||
        isModels3DRoute ||
        isMyLibraryRoute ||
        isNewHandoutsRoute ||
        isMyClinicRoute ||
        isHandoutsRoute ||
        isHelpHubRoute ||
        location.pathname.startsWith('/settings');
    const isHandoutViewRoute = location.pathname.startsWith('/handout/');
    const profileImageSrc = userDataStore?.userData?.clinicImageId || '';

    useEffect(() => {
        if (!searchOpen) return;
        const handleClickOutside = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) {
                setSearchOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [searchOpen]);

    const handleNavigate = (path) => {
        promptUnsavedChanges(path);
        setMobileOpen(false);
    };

    const handleDiagramsClick = () => {
        setMobileOpen(false);
        promptBeforeAction(() => navigate('/search', { state: { category: 'Diagrams' } }));
    };

    const handleHandoutLibraryClick = () => {
        setMobileOpen(false);
        promptBeforeAction(() => navigate('/search', { state: { resetFilters: true } }));
    };

    const handleHandoutsClick = () => {
        const upgradeTarget = getHandoutBundleUpgradeTarget(userDataStore?.subscriptionData?.accountType);

        if (upgradeTarget === 'professional') {
            setUpgradeCTAOpenProfessional(true);
            return;
        }

        if (upgradeTarget === 'clinic') {
            setUpgradeCTAOpen(true);
            return;
        }

        handleNavigate('/handouts');
    };

    const handleMyWorkspaceClick = () => {
        if (isFreeOrStudentPlan(userDataStore?.subscriptionData?.accountType)) {
            setUpgradeCTAOpenProfessional(true);
            return;
        }
        handleNavigate('/my-library');
    };

    const handleMyClinicClick = () => {
        if (isFreeOrStudentPlan(userDataStore?.subscriptionData?.accountType)) {
            setUpgradeCTAOpenProfessional(true);
            return;
        }
        handleNavigate('/my-clinic');
    };

    const handleCloseMenus = () => {
        setProfileMenuAnchor(null);
        setWorkspaceMenuAnchor(null);
    };

    const handleProfileMenuOpen = (event) => {
        event.stopPropagation();
        event.preventDefault();
        setProfileMenuAnchor(event.currentTarget);
    };

    const handleProfileMenuNavigate = (path) => {
        handleCloseMenus();
        handleNavigate(path);
    };

    const handleWorkspaceMenuOpen = (event) => {
        event.stopPropagation();
        event.preventDefault();
        setWorkspaceMenuAnchor(event.currentTarget);
    };

    const handleWorkspaceMyHandoutsClick = () => {
        setWorkspaceMenuAnchor(null);
        handleMyWorkspaceClick();
    };

    const handleWorkspaceDesignHandoutClick = () => {
        setWorkspaceMenuAnchor(null);
        handleNavigate('/build-handout');
    };

    const handleWorkspacePatientRecordsClick = () => {
        setWorkspaceMenuAnchor(null);
        handleMyClinicClick();
    };

    const handleLogout = async () => {
        handleCloseMenus();
        await authStore.logout();
        await appStateStore.clearUserStores();
        localStorage.clear();
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleAccessibilityToggle = (event, isMobile = false) => {
        const triggerRect = event.currentTarget.getBoundingClientRect();
        window.dispatchEvent(
            new CustomEvent('cetra:toggle-accessibility', {
                detail: { triggerRect, isMobile },
            }),
        );
    };

    const handleSearchInputChange = (value) => {
        searchStore.setRawSearchTerm(value);
        if (value.trim() && contentStore?.isInitialized) {
            const term = value.trim();
            const results = [
                ...contentStore.searchContentMeta(term),
                ...(userCreatedHandoutStore?.searchContentMeta(term) || []),
            ];
            searchStore.setSearchResults(results);
        } else {
            searchStore.setSearchResults([]);
        }
    };

    useEffect(() => {
        const term = searchStore?.rawSearchTerm?.trim();
        if (!term || !contentStore?.isInitialized) {
            searchStore?.setSearchResults([]);
            return;
        }

        const results = [
            ...contentStore.searchContentMeta(term),
            ...(userCreatedHandoutStore?.searchContentMeta(term) || []),
        ];
        searchStore.setSearchResults(results);
    }, [
        contentStore,
        contentStore?.isInitialized,
        searchStore,
        searchStore?.rawSearchTerm,
        userCreatedHandoutStore,
        userCreatedHandoutStore?.isInitialized,
        userCreatedHandoutStore?.userCreatedHandoutData,
    ]);

    const getRecentSearches = () => {
        if (Array.isArray(userDataStore?.userData?.recentSearches)) {
            return userDataStore.userData.recentSearches;
        }
        return [];
    };

    const persistRecentSearches = async (recentSearches) => {
        userDataStore.setUserData({
            ...userDataStore.userData,
            recentSearches,
        });

        if (!authStore?.idToken) {
            return;
        }

        try {
            if (isMultiUser) {
                if (!userDataStore?.userData?.inviteId || !userDataStore?.userData?.emailAddress) {
                    return;
                }
                await UserApi.updateClinicianDataMultiuser(authStore.idToken, {
                    action: 'updateMultiuser',
                    inviteId: userDataStore.userData.inviteId,
                    emailAddress: userDataStore.userData.emailAddress,
                    recentSearches,
                });
            } else {
                await UserApi.updateClinicianData(authStore.idToken, { recentSearches });
            }
        } catch (error) {
            console.error('Failed to persist recent searches:', error);
        }
    };

    const addRecentSearch = async (searchValue) => {
        const term = (searchValue || '').trim();
        if (!term) {
            return;
        }

        const currentRecentSearches = getRecentSearches();
        const nextRecentSearches = [
            term,
            ...currentRecentSearches.filter((entry) => entry.toLowerCase() !== term.toLowerCase()),
        ].slice(0, RECENT_SEARCHES_LIMIT);

        await persistRecentSearches(nextRecentSearches);
    };

    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            setSearchOpen(false);
            handleNavigate('/search');
        }
    };

    const handleSearchArrowClick = () => {
        setSearchOpen(false);
        handleNavigate('/search');
    };

    const handleClearRecentSearches = async () => {
        await persistRecentSearches([]);
    };

    const handleRecentSearchClick = (term) => {
        handleSearchInputChange(term);
    };

    const handleSearchResultClick = (result) => {
        void addRecentSearch(result.contentName);
        setSearchOpen(false);
        if (result.contentType === 'userCreatedHandout') {
            navigate(`/my-handout/${result.handoutId || result.contentId}`);
            return;
        }
        navigate(`/handout/${result.contentId}`, { state: { breadcrumbs: ['SEARCH'] } });
    };

    const navLinkSx = {
        fontFamily: 'Inter, sans-serif',
        fontWeight: 500,
        fontSize: '18px',
        lineHeight: '18px',
        color: '#000453',
        letterSpacing: '-0.72px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        background: 'none',
        border: 'none',
        padding: '4px 0',
        '&:hover': { opacity: 0.75 },
    };

    const recentSearches = getRecentSearches();
    const hasRecentSearches = recentSearches.length > 0;
    const trimmedSearchTerm = (searchStore.rawSearchTerm || '').trim();
    const searchResults = trimmedSearchTerm ? (searchStore.searchResults || []).slice(0, 5) : [];
    const showDropdownPanel = searchOpen && (hasRecentSearches || !!trimmedSearchTerm);

    const renderSearchBar = () => (
        <Box ref={searchRef} sx={{ position: 'relative', paddingBottom: { xs: '6px', md: '9px' } }}>
            <Box
                onClick={!searchOpen ? () => setSearchOpen(true) : undefined}
                sx={{
                    height: { xs: '40px', sm: '48px', md: '56px' },
                    borderRadius: '100px',
                    background: searchOpen
                        ? '#FFFFFF'
                        : 'linear-gradient(176.249deg, rgb(255, 102, 155) 0%, rgb(189, 141, 232) 110.58%)',
                    border: searchOpen ? '1px solid #FA96C3' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: { xs: '16px', md: '24px' },
                    paddingRight: { xs: '6px', md: '8px' },
                    cursor: searchOpen ? 'default' : 'text',
                    width: '100%',
                    maxWidth: { xs: '100%', md: '1000px' },
                }}
            >
                <SearchIcon
                    sx={{
                        color: searchOpen ? '#616063' : 'white',
                        opacity: searchOpen ? 1 : 0.8,
                        mr: 1.5,
                        flexShrink: 0,
                        fontSize: { xs: 18, md: 24 },
                    }}
                />
                {searchOpen ? (
                    <>
                        <InputBase
                            autoFocus
                            fullWidth
                            placeholder="Search by condition or body part"
                            value={searchStore.rawSearchTerm || ''}
                            onChange={(e) => handleSearchInputChange(e.target.value)}
                            onKeyPress={handleSearchKeyPress}
                            sx={{
                                '& .MuiInputBase-input': {
                                    color: '#616063',
                                    fontSize: '16px',
                                    fontFamily: 'Inter, sans-serif',
                                    '&::placeholder': { color: '#B4B4B4', opacity: 1 },
                                },
                            }}
                        />
                        {trimmedSearchTerm && (
                            <IconButton
                                aria-label="Submit search"
                                onClick={handleSearchArrowClick}
                                sx={{
                                    width: 23,
                                    height: '100%',
                                    mr: { xs: '14px', md: '20px' },
                                    p: 0,
                                    color: '#616063',
                                    flexShrink: 0,
                                    '&:hover': { backgroundColor: 'transparent' },
                                }}
                            >
                                <img
                                    src={searchSubmitArrowIcon}
                                    alt=""
                                    width={23}
                                    height={16}
                                    style={{ display: 'block' }}
                                />
                            </IconButton>
                        )}
                    </>
                ) : (
                    <Typography
                        sx={{
                            color: 'white',
                            opacity: 0.8,
                            fontSize: { xs: '14px', md: '16px' },
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 400,
                            letterSpacing: '-0.04em',
                        }}
                    >
                        Search by condition or body part
                    </Typography>
                )}
            </Box>

            {showDropdownPanel && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: { xs: '52px', sm: '56px', md: '60px' },
                        left: 0,
                        right: 0,
                        maxWidth: '1000px',
                        background: 'white',
                        borderRadius: '20px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        padding: { xs: '12px 16px', md: '20px 0 8px' },
                        zIndex: 1200,
                    }}
                >
                    {hasRecentSearches && (
                        <>
                            <Box
                                sx={{
                                    px: { xs: 2, md: '47px' },
                                    pb: '8px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Typography
                                        sx={{
                                            color: '#616063',
                                            fontSize: '16px',
                                            lineHeight: '16px',
                                            fontWeight: 500,
                                        }}
                                    >
                                        Recent searches
                                    </Typography>
                                    <Box
                                        component="img"
                                        src={clockIcon}
                                        alt="Recent searches"
                                        sx={{ width: 14, height: 14 }}
                                    />
                                </Box>
                                <Box
                                    component="button"
                                    onClick={handleClearRecentSearches}
                                    sx={{
                                        border: 'none',
                                        background: 'transparent',
                                        color: '#FA96C3',
                                        fontSize: '16px',
                                        lineHeight: '16px',
                                        cursor: 'pointer',
                                        p: 0,
                                        fontFamily: 'Inter, sans-serif',
                                    }}
                                >
                                    Clear
                                </Box>
                            </Box>
                            <Divider sx={{ mx: { xs: 2, md: '47px' } }} />
                            <List disablePadding sx={{ py: 1 }}>
                                {recentSearches.map((term, index) => (
                                    <ListItemButton
                                        key={`${term}-${index}`}
                                        onClick={() => handleRecentSearchClick(term)}
                                        sx={{
                                            px: { xs: 2, md: '47px' },
                                            py: '6px',
                                            minHeight: 0,
                                        }}
                                    >
                                        <Typography sx={{ color: '#616063', fontSize: '16px', lineHeight: '16px' }}>
                                            {term}
                                        </Typography>
                                    </ListItemButton>
                                ))}
                            </List>
                        </>
                    )}

                    {hasRecentSearches && !!trimmedSearchTerm && <Divider sx={{ mx: { xs: 2, md: '47px' } }} />}

                    {!!trimmedSearchTerm && (
                        <List disablePadding sx={{ py: 1 }}>
                            {searchResults.map((result, index) => (
                                <ListItemButton
                                    key={`${result.contentId}-${index}`}
                                    onClick={() => handleSearchResultClick(result)}
                                    sx={{
                                        px: { xs: 2, md: '47px' },
                                        py: '6px',
                                        minHeight: 0,
                                        borderRadius: '999px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        '& .result-arrow': { opacity: 0, transition: 'opacity 0.15s ease' },
                                        '&:hover': { backgroundColor: '#F1F1F1' },
                                        '&:hover .result-arrow': { opacity: 1 },
                                    }}
                                >
                                    <Typography sx={{ color: '#616063', fontSize: '16px', lineHeight: '16px' }}>
                                        {result.contentName}
                                    </Typography>
                                    <EastIcon className="result-arrow" sx={{ fontSize: 16, color: '#616063' }} />
                                </ListItemButton>
                            ))}
                            {searchResults.length === 0 && (
                                <Typography
                                    sx={{
                                        px: { xs: 2, md: '47px' },
                                        py: '6px',
                                        color: '#9A9A9A',
                                        fontSize: '14px',
                                    }}
                                >
                                    No matching handouts
                                </Typography>
                            )}
                        </List>
                    )}
                </Box>
            )}
        </Box>
    );

    const renderDesktopNavigation = () => (
        <Box display="flex" flexDirection="column" width="100%" height="100%" justifyContent="space-between">
            {/* Row 1: Logo + Nav items (left group) | spacer | Right icons */}
            <Box display="flex" alignItems="center" justifyContent="space-between" flex={1} minHeight={0}>
                {/* Left group: Logo + Nav items */}
                <Box display="flex" alignItems="center" gap={{ md: '24px', lg: '32px' }} minWidth={0}>
                    {/* Logo */}
                    <Box
                        component="button"
                        onClick={() => handleNavigate('/')}
                        sx={{
                            p: 0,
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <img src={cetraBlueLogo} alt="Home" width={120} />
                    </Box>

                    {/* Nav items */}
                    <Box display="flex" alignItems="center" gap={{ md: '20px', lg: '32px' }} minWidth={0}>
                        <Box component="button" onClick={handleHandoutLibraryClick} sx={navLinkSx}>
                            Handout Library
                        </Box>
                        <Box component="button" onClick={() => handleDiagramsClick()} sx={navLinkSx}>
                            Diagrams
                        </Box>
                        <Box
                            component="button"
                            onClick={handleWorkspaceMenuOpen}
                            sx={{
                                ...navLinkSx,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                            }}
                        >
                            Workspace
                            <KeyboardArrowDownIcon sx={{ fontSize: 18, color: '#000453' }} />
                        </Box>
                    </Box>
                </Box>

                {/* Right icons */}
                <Box display="flex" alignItems="center" gap="12px" flexShrink={0}>
                    <IconButton
                        disableRipple
                        onClick={(event) => handleAccessibilityToggle(event, false)}
                        className="accessibility-trigger"
                        sx={{
                            padding: 0,
                            width: 40,
                            height: 40,
                            background: '#F6F6F6',
                            borderRadius: '360px',
                        }}
                        aria-label="Accessibility menu"
                    >
                        <img src={accessibilityIcon} alt="Accessibility" width={20} height={23} />
                    </IconButton>

                    {/* Feedback Star */}
                    <IconButton
                        disableRipple
                        sx={{ padding: 0, width: 40, height: 40 }}
                        aria-label="Feedback"
                        onClick={() => setFeedbackOpen(true)}
                    >
                        <img src={feedbackStarIcon} alt="Feedback" width={40} height={40} />
                    </IconButton>

                    {/* Bundle / Handout */}
                    <Badge
                        badgeContent={handoutBundleCount || null}
                        sx={{
                            '& .MuiBadge-badge': {
                                backgroundColor: '#FC96A2',
                                color: 'white',
                                transform: 'translateZ(1px)',
                            },
                        }}
                        overlap="circular"
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        <IconButton
                            disableRipple
                            onClick={handleHandoutsClick}
                            sx={{ padding: 0, width: 40, height: 40 }}
                            aria-label="Bundle"
                        >
                            <img src={handoutIcon} alt="Bundle" width={40} height={40} />
                        </IconButton>
                    </Badge>

                    {/* Profile button */}
                    <Box
                        component="button"
                        onClick={handleProfileMenuOpen}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5.722px',
                            height: '40px',
                            background: '#F6F6F6',
                            borderRadius: '257px',
                            border: 'none',
                            cursor: 'pointer',
                            paddingLeft: '6px',
                            paddingRight: '11px',
                        }}
                    >
                        {profileImageSrc ? (
                            <Box
                                sx={{
                                    width: 30,
                                    height: 30,
                                    borderRadius: '50%',
                                    border: '2px solid rgba(32, 31, 32, 0.8)',
                                    overflow: 'hidden',
                                    flexShrink: 0,
                                }}
                            >
                                <img
                                    src={profileImageSrc}
                                    alt="Practice"
                                    width={30}
                                    height={30}
                                    style={{ display: 'block', objectFit: 'cover' }}
                                />
                            </Box>
                        ) : (
                            <img src={profilIcon} alt="Profile" width={30} height={30} />
                        )}
                        <Typography
                            sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 500,
                                fontSize: '16px',
                                color: '#201F20',
                                opacity: 0.8,
                                letterSpacing: '-0.8px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {userDataStore?.subscriptionData?.accountType?.includes('Clinic') &&
                            userDataStore?.userData?.clinicName
                                ? userDataStore.userData.clinicName
                                : 'Profile'}
                        </Typography>
                        <KeyboardArrowDownIcon
                            fontSize="small"
                            sx={{
                                transition: 'transform 0.2s ease',
                                transform: profileMenuAnchor ? 'rotate(180deg)' : 'rotate(0deg)',
                                color: '#201F20',
                                opacity: 0.8,
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Row 2: Search bar */}
            {!isCompactHeaderRoute && renderSearchBar()}
        </Box>
    );

    return (
        <>
            <AppBar
                position="relative"
                color="default"
                sx={{
                    boxShadow: 'none',
                    background: 'white',
                    padding: 0,
                    zIndex: 1000,
                    overflow: 'visible',
                    height: showBurgerMenu ? 'auto' : isCompactHeaderRoute ? '83px' : `${HEADER_HEIGHT}px`,
                    marginBottom: '0',
                    '@media print': { display: 'none' },
                }}
            >
                {/* Full-width container with fixed side gutters */}
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        padding: { xs: '12px 20px', md: isCompactHeaderRoute ? '9px 52px' : '9px 40px' },
                        boxSizing: 'border-box',
                    }}
                >
                    {isIframe && (
                        <Box display="flex" alignItems="center" mt={1}>
                            <img src={cetraBlueLogo} alt="Home" width={120} />
                        </Box>
                    )}

                    {!isIframe && (
                        <>
                            {showBurgerMenu ? (
                                <Box display="flex" flexDirection="column" gap="12px">
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Box display="flex" alignItems="center">
                                            <Box
                                                component="button"
                                                sx={{
                                                    p: 0,
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    mr: 2.5,
                                                    display: 'flex',
                                                }}
                                                onClick={() => handleNavigate('/')}
                                            >
                                                <img src={cetraMobileLogo} alt="Home" width={40} />
                                            </Box>
                                            <IconButton
                                                color="inherit"
                                                aria-label="open drawer"
                                                edge="start"
                                                onClick={handleDrawerToggle}
                                                sx={{ backgroundColor: 'rgb(246, 246, 246)' }}
                                            >
                                                <MenuIcon />
                                            </IconButton>

                                            {mobileOpen && (
                                                <>
                                                    <Box
                                                        sx={{
                                                            position: 'fixed',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                            zIndex: 1998,
                                                        }}
                                                        onClick={handleDrawerToggle}
                                                    />
                                                    <Box
                                                        sx={{
                                                            position: 'fixed',
                                                            top: 0,
                                                            left: 0,
                                                            width: '70%',
                                                            maxWidth: '300px',
                                                            height: '100vh',
                                                            bgcolor: 'background.paper',
                                                            zIndex: 1999,
                                                            boxShadow: 3,
                                                        }}
                                                    >
                                                        <IconButton
                                                            onClick={handleDrawerToggle}
                                                            sx={{
                                                                position: 'absolute',
                                                                top: '16px',
                                                                left: '10px',
                                                                zIndex: 1000,
                                                                background: 'rgb(246, 246, 246)',
                                                            }}
                                                        >
                                                            <CloseIcon />
                                                        </IconButton>
                                                        <List sx={{ marginTop: '80px' }}>
                                                            <ListItem
                                                                button
                                                                onClick={handleHandoutLibraryClick}
                                                                sx={{ borderBottom: '1px solid #e0e0e0' }}
                                                                secondaryAction={
                                                                    <ChevronRightIcon
                                                                        color="action"
                                                                        sx={{ fontSize: 16 }}
                                                                    />
                                                                }
                                                            >
                                                                <ListItemText primary="Handout Library" />
                                                            </ListItem>
                                                            <ListItem
                                                                button
                                                                onClick={() => handleDiagramsClick()}
                                                                sx={{ borderBottom: '1px solid #e0e0e0' }}
                                                                secondaryAction={
                                                                    <ChevronRightIcon
                                                                        color="action"
                                                                        sx={{ fontSize: 16 }}
                                                                    />
                                                                }
                                                            >
                                                                <ListItemText primary="Diagrams" />
                                                            </ListItem>
                                                            <ListItem sx={{ borderBottom: '1px solid #e0e0e0' }}>
                                                                <ListItemText primary="Workspace" />
                                                            </ListItem>
                                                            <ListItem
                                                                button
                                                                onClick={handleMyWorkspaceClick}
                                                                sx={{ borderBottom: '1px solid #e0e0e0', pl: 4 }}
                                                                secondaryAction={
                                                                    <ChevronRightIcon
                                                                        color="action"
                                                                        sx={{ fontSize: 16 }}
                                                                    />
                                                                }
                                                            >
                                                                <ListItemText primary="My handouts" />
                                                            </ListItem>
                                                            <ListItem
                                                                button
                                                                onClick={handleWorkspaceDesignHandoutClick}
                                                                sx={{ borderBottom: '1px solid #e0e0e0', pl: 4 }}
                                                                secondaryAction={
                                                                    <ChevronRightIcon
                                                                        color="action"
                                                                        sx={{ fontSize: 16 }}
                                                                    />
                                                                }
                                                            >
                                                                <ListItemText primary="Build a handout" />
                                                            </ListItem>
                                                            <ListItem
                                                                button
                                                                onClick={handleMyClinicClick}
                                                                sx={{ borderBottom: '1px solid #e0e0e0', pl: 4 }}
                                                                secondaryAction={
                                                                    <ChevronRightIcon
                                                                        color="action"
                                                                        sx={{ fontSize: 16 }}
                                                                    />
                                                                }
                                                            >
                                                                <ListItemText primary="Patient records" />
                                                            </ListItem>
                                                        </List>
                                                    </Box>
                                                </>
                                            )}
                                        </Box>

                                        <Box display="flex" alignItems="center" gap="12px" flexShrink={0}>
                                            <IconButton
                                                disableRipple
                                                onClick={(event) => handleAccessibilityToggle(event, true)}
                                                className="accessibility-trigger"
                                                sx={{
                                                    padding: 0,
                                                    width: 40,
                                                    height: 40,
                                                    background: '#F6F6F6',
                                                    borderRadius: '360px',
                                                }}
                                                aria-label="Accessibility menu"
                                            >
                                                <img
                                                    src={accessibilityIcon}
                                                    alt="Accessibility"
                                                    width={20}
                                                    height={23}
                                                />
                                            </IconButton>

                                            <IconButton
                                                disableRipple
                                                sx={{ padding: 0, width: 40, height: 40 }}
                                                aria-label="Feedback"
                                                onClick={() => setFeedbackOpen(true)}
                                            >
                                                <img src={feedbackStarIcon} alt="Feedback" width={40} height={40} />
                                            </IconButton>
                                            <Badge
                                                badgeContent={handoutBundleCount || null}
                                                sx={{
                                                    '& .MuiBadge-badge': { backgroundColor: '#FC96A2', color: 'white' },
                                                }}
                                                overlap="circular"
                                                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                            >
                                                <IconButton
                                                    disableRipple
                                                    onClick={handleHandoutsClick}
                                                    sx={{ padding: 0, width: 40, height: 40 }}
                                                >
                                                    <img src={handoutIcon} alt="Bundle" width={40} height={40} />
                                                </IconButton>
                                            </Badge>
                                            <IconButton
                                                onClick={handleProfileMenuOpen}
                                                sx={{
                                                    height: 40,
                                                    width: 40,
                                                    background: '#F6F6F6',
                                                    borderRadius: '36px',
                                                    padding: 0,
                                                }}
                                            >
                                                {profileImageSrc ? (
                                                    <Box
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: '36px',
                                                            overflow: 'hidden',
                                                        }}
                                                    >
                                                        <img
                                                            width={40}
                                                            height={40}
                                                            style={{ display: 'block', objectFit: 'cover' }}
                                                            src={profileImageSrc}
                                                            alt="Practice"
                                                        />
                                                    </Box>
                                                ) : (
                                                    <img src={profilIcon} alt="Profile" width={25} height={25} />
                                                )}
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    {!isCompactHeaderRoute && !isHandoutViewRoute && renderSearchBar()}
                                </Box>
                            ) : (
                                renderDesktopNavigation()
                            )}
                        </>
                    )}
                </Box>
            </AppBar>

            <Menu
                anchorEl={workspaceMenuAnchor}
                open={Boolean(workspaceMenuAnchor)}
                onClose={() => setWorkspaceMenuAnchor(null)}
                keepMounted
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <MenuItem onClick={handleWorkspaceMyHandoutsClick}>My handouts</MenuItem>
                <MenuItem onClick={handleWorkspaceDesignHandoutClick}>Build a handout</MenuItem>
                <MenuItem onClick={handleWorkspacePatientRecordsClick}>Patient records</MenuItem>
            </Menu>

            <Menu
                anchorEl={profileMenuAnchor}
                open={Boolean(profileMenuAnchor)}
                onClose={handleCloseMenus}
                keepMounted
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={() => handleProfileMenuNavigate(isMultiUser ? '/settings-multiuser' : '/settings')}>
                    Settings
                </MenuItem>
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>

            <CTAUpgradeDialog
                open={upgradeCTAOpen}
                handleClose={() => setUpgradeCTAOpen(false)}
                onConfirm={() => navigate('/settings/manage-subscription')}
                title="Upgrade now for clinic-wide access and advanced features!"
                message="You've reached a feature that's beyond your current plan. Upgrade today to add more users and enjoy full access to our features!"
                buttonText="Upgrade plan"
                benefits={[
                    'Unlimited digital sharing of all handouts & anatomy diagrams',
                    'Bundling of handouts and diagrams for seamless sharing',
                    'Personalise with your clinic logo',
                ]}
                imageUrl={getCTAUpgradeImageUrl('clinic')}
            />

            <CTAUpgradeDialog
                open={upgradeCTAOpenProfessional}
                handleClose={() => setUpgradeCTAOpenProfessional(false)}
                onConfirm={() => navigate('/settings/manage-subscription')}
                title="Unlock unlimited sharing and more with just one click!"
                message="You've reached a feature that's beyond your current plan. Upgrade today to enjoy full access!"
                buttonText="Upgrade plan"
                benefits={[
                    'Unlimited digital sharing of all handouts',
                    'Unlimited digital sharing of all anatomy diagrams',
                    'Personalise with your clinic details ',
                ]}
                imageUrl={getCTAUpgradeImageUrl('professional')}
            />

            <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
        </>
    );
});

export default Navigation;
